import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { FiCalendar } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import { SAMPLE_SECTORS } from '../constants/sampleSectors';
import { apiUrl } from '../utils/api';
import InlineLoader from './InlineLoader';

const buildSampleProtocol = (sector, numericPart) => {
  const normalizedSector = String(sector || '').trim().toUpperCase();
  const digits = String(numericPart || '').replace(/\D/g, '');

  if (!normalizedSector || !digits) {
    return '';
  }

  const prefix = normalizedSector === 'PACK_TORO' ? 'TP' : normalizedSector;
  return `${prefix}-${digits.padStart(3, '0')}`;
};

const extractProtocolNumber = (protocol) => {
  const value = String(protocol || '').trim();
  const parts = value.split('-');
  return parts.length > 1 ? String(parts[parts.length - 1] || '').replace(/\D/g, '') : '';
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};

const getUserDisplayName = (user = {}) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.username || user.email || 'Usuario sin nombre';
};

const FIELD_LABELS = {
  identificacion: 'Identificación de muestra / animal',
  protocolo_general: 'Protocolo general',
  protocolo_muestra: 'Protocolo de muestra',
  sector: 'Sector',
  fecha_recoleccion: 'Fecha de recolección',
  aisa_tomo_muestra: 'AISA tomó muestra',
  nombre_operario_INTA: 'Operario INTA',
  grupo_zootecnico: 'Grupo zootécnico',
  cantidad_grupos_zootecnicos: 'Cantidad de grupos zootécnicos',
  tipo_muestra: 'Tipo de muestra',
  tipo_muestra_vegetal: 'Tipo de muestra vegetal',
  tipo_muestra_animal: 'Tipo de muestra animal',
  otro_tipo_muestra: 'Otro tipo de muestra',
  numero_de_muestras: 'Número de muestras',
  observaciones: 'Observaciones',
  descripcion_vegetal: 'Descripcion vegetal',
  non_field_errors: 'Formulario',
  detail: 'Detalle',
};

const RECENT_PROTOCOL_LIMIT = 20;
const SAMPLE_OBSERVATIONS_MAX_LENGTH = 2000;
const protocolCollator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const getLastWeekRange = () => {
  const hasta = new Date();
  const desde = new Date(hasta);
  desde.setDate(desde.getDate() - 6);
  return { desde: formatLocalDate(desde), hasta: formatLocalDate(hasta) };
};

const formatSampleErrorMessage = (errorData) => {
  if (!errorData) {
    return 'No se pudo guardar la muestra.';
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  const messages = Object.entries(errorData)
    .flatMap(([field, value]) => {
      const label = FIELD_LABELS[field] || field;
      const normalizedValue = Array.isArray(value) ? value : [value];

      return normalizedValue
        .flatMap((entry) => {
          if (entry === null || entry === undefined || entry === '') {
            return [];
          }

          if (typeof entry === 'string') {
            return `${label}: ${entry}`;
          }

          if (typeof entry === 'object') {
            return JSON.stringify(entry);
          }

          return `${label}: ${String(entry)}`;
        });
    })
    .filter(Boolean);

  return messages.length > 0 ? messages.join(' | ') : 'No se pudo guardar la muestra.';
};

const normalizeSampleData = (sampleData = {}, fallbackProtocol = '') => ({
  identificacion: sampleData.identificacion || '',
  protocolo_general: sampleData.protocolo_general || fallbackProtocol || '',
  fecha_recoleccion: sampleData.fecha_recoleccion || null,
  aisa_tomo_muestra: Boolean(sampleData.aisa_tomo_muestra),
  nombre_operario_INTA: sampleData.nombre_operario_INTA || '',
  grupo_zootecnico: sampleData.grupo_zootecnico || '',
  cantidad_grupos_zootecnicos: sampleData.cantidad_grupos_zootecnicos || '',
  tipo_muestra: sampleData.tipo_muestra || '',
  tipo_muestra_vegetal: sampleData.tipo_muestra_vegetal || '',
  tipo_muestra_animal: sampleData.tipo_muestra_animal || '',
  otro_tipo_muestra: sampleData.otro_tipo_muestra || '',
  numero_de_muestras: sampleData.numero_de_muestras || 1,
  observaciones: sampleData.observaciones || '',
  descripcion_vegetal: sampleData.descripcion_vegetal || '',
  sector: sampleData.sector || '',
  protocolo_muestra: sampleData.protocolo_muestra || '',
});

const IngresoMuestraForm = ({ sampleId = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(sampleId);
  const protocoloGeneralInicial = location.state?.protocoloGeneral || '';
  const returnToCaseDetail = Boolean(location.state?.returnToCaseDetail || isEditMode);
  const [formData, setFormData] = useState(() => normalizeSampleData({}, protocoloGeneralInicial));
  const [protocoloMuestraEditable, setProtocoloMuestraEditable] = useState(false);
  const [numeroProtocoloManual, setNumeroProtocoloManual] = useState('');
  const [checkingProtocol, setCheckingProtocol] = useState(false);
  const [protocolExists, setProtocolExists] = useState(false);
  const [protocolosDisponibles, setProtocolosDisponibles] = useState([]);
  const [rangoProtocolos, setRangoProtocolos] = useState(getLastWeekRange);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(!protocoloGeneralInicial);
  const [loadingSample, setLoadingSample] = useState(isEditMode);
  const [submitNotice, setSubmitNotice] = useState(null);
  const operarioExistsInOptions = usuarios.some((user) => user.username === formData.nombre_operario_INTA);
  const operarioLegacyLabel = formData.nombre_operario_INTA ? `Valor actual: ${formData.nombre_operario_INTA}` : '';
  const protocoloMuestraPreview = formData.protocolo_muestra || buildSampleProtocol(formData.sector, numeroProtocoloManual);
  const observacionesLength = String(formData.observaciones || '').length;

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    setFormData((prev) => ({ ...prev, protocolo_general: protocoloGeneralInicial }));
  }, [isEditMode, protocoloGeneralInicial]);

  useEffect(() => {
    let active = true;

    if (!protocoloGeneralInicial) {
      setLoading(true);
    }
    axios
      .get(apiUrl(`/api/ingresos_casos/?compact=1&desde=${encodeURIComponent(rangoProtocolos.desde)}&hasta=${encodeURIComponent(rangoProtocolos.hasta)}`))
      .then((response) => {
        if (!active) {
          return;
        }

        const casos = Array.isArray(response.data) ? response.data : [];
        const protocolos = casos
          .slice(0, RECENT_PROTOCOL_LIMIT)
          .map((caso) => caso.protocolo_general)
          .filter(Boolean)
          .sort((a, b) => protocolCollator.compare(b, a));
        setProtocolosDisponibles(protocolos);
      })
      .catch((error) => console.error('Error al obtener protocolos:', error))
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [protocoloGeneralInicial, rangoProtocolos.desde, rangoProtocolos.hasta]);

  useEffect(() => {
    let active = true;

    axios
      .get(apiUrl('/api/usuarios/opciones/?solo_operarios=1'), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        setUsuarios(response.data || []);
      })
      .catch((error) => console.error('Error al obtener usuarios:', error));

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      setLoadingSample(false);
      return;
    }

    let active = true;
    setLoadingSample(true);
    setSubmitNotice(null);

    axios
      .get(apiUrl(`/api/ingresos_muestras/${sampleId}/`), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        setFormData(normalizeSampleData(response.data));
        setNumeroProtocoloManual(extractProtocolNumber(response.data?.protocolo_muestra));
      })
      .catch((error) => {
        console.error('Error al cargar la muestra:', error);
        if (!active) {
          return;
        }

        const message = 'No se pudo cargar la muestra para editar.';
        setSubmitNotice({ type: 'error', text: message });
        toast.error(message);
      })
      .finally(() => {
        if (active) {
          setLoadingSample(false);
        }
      });

    return () => {
      active = false;
    };
  }, [isEditMode, sampleId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      if (name === 'tipo_muestra') {
        return {
          ...prev,
          tipo_muestra: value,
          grupo_zootecnico: value === 'ANIMAL' ? prev.grupo_zootecnico : '',
          cantidad_grupos_zootecnicos: value === 'ANIMAL' ? prev.cantidad_grupos_zootecnicos : '',
          tipo_muestra_vegetal: value === 'VEGETAL' ? prev.tipo_muestra_vegetal : '',
          tipo_muestra_animal: value === 'ANIMAL' ? prev.tipo_muestra_animal : '',
          descripcion_vegetal: value === 'VEGETAL' ? prev.descripcion_vegetal : '',
          otro_tipo_muestra: value === 'OTRO' ? prev.otro_tipo_muestra : '',
        };
      }

      if (name === 'grupo_zootecnico') {
        return {
          ...prev,
          grupo_zootecnico: value,
          cantidad_grupos_zootecnicos: value === 'VARIOS' ? prev.cantidad_grupos_zootecnicos : '',
        };
      }

      if (name === 'tipo_muestra_vegetal' && value !== 'OTRO') {
        return {
          ...prev,
          tipo_muestra_vegetal: value,
          descripcion_vegetal: '',
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleSectorChange = async (event) => {
    const selectedSector = String(event.target.value);
    setFormData((prev) => ({
      ...prev,
      sector: selectedSector,
      protocolo_muestra: protocoloMuestraEditable
        ? buildSampleProtocol(selectedSector, numeroProtocoloManual)
        : prev.protocolo_muestra,
    }));

    if (!selectedSector || isEditMode || protocoloMuestraEditable) {
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/next-protocolo/?sector=${selectedSector}`));
      const data = await response.json();
      setFormData((prev) => ({ ...prev, protocolo_muestra: data.protocolo || '' }));
    } catch (error) {
      console.error('Error al obtener protocolo:', error);
    }
  };

  const handleNumeroProtocoloManualChange = (event) => {
    const digitsOnly = String(event.target.value || '').replace(/\D/g, '').slice(0, 5);
    setNumeroProtocoloManual(digitsOnly);
    setFormData((prev) => ({
      ...prev,
      protocolo_muestra: buildSampleProtocol(prev.sector, digitsOnly),
    }));
  };

  useEffect(() => {
    if (isEditMode || !protocoloMuestraEditable) {
      setCheckingProtocol(false);
      setProtocolExists(false);
      return;
    }

    const protocolo = buildSampleProtocol(formData.sector, numeroProtocoloManual);
    if (!protocolo) {
      setCheckingProtocol(false);
      setProtocolExists(false);
      return;
    }

    let active = true;
    setCheckingProtocol(true);

    axios
      .get(apiUrl(`/api/verificar_protocolo_muestra/${encodeURIComponent(protocolo)}/`), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        setProtocolExists(Boolean(response.data?.existe));
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        console.error('Error al verificar protocolo de muestra:', error);
        setProtocolExists(false);
      })
      .finally(() => {
        if (active) {
          setCheckingProtocol(false);
        }
      });

    return () => {
      active = false;
    };
  }, [formData.sector, isEditMode, numeroProtocoloManual, protocoloMuestraEditable]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitNotice(null);

    const protocoloMuestraFinal = protocoloMuestraEditable
      ? buildSampleProtocol(formData.sector, numeroProtocoloManual)
      : String(formData.protocolo_muestra || '').trim();

    if (!isEditMode && protocoloMuestraEditable) {
      if (!formData.sector) {
        const message = 'Selecciona un sector para definir las iniciales del protocolo.';
        setSubmitNotice({ type: 'error', text: message });
        toast.error(message);
        return;
      }

      if (!numeroProtocoloManual) {
        const message = 'Ingresa el numero del protocolo de muestra.';
        setSubmitNotice({ type: 'error', text: message });
        toast.error(message);
        return;
      }

      if (numeroProtocoloManual.length < 3 || numeroProtocoloManual.length > 5) {
        const message = 'El numero del protocolo de muestra debe tener entre 3 y 5 digitos.';
        setSubmitNotice({ type: 'error', text: message });
        toast.error(message);
        return;
      }

      if (protocolExists) {
        const message = `El protocolo de muestra ${protocoloMuestraFinal} ya existe.`;
        setSubmitNotice({ type: 'error', text: message });
        toast.error(message);
        return;
      }
    }

    const cleanedData = {
      ...formData,
      protocolo_general: String(formData.protocolo_general || '').trim(),
      protocolo_muestra: protocoloMuestraFinal,
      sector: String(formData.sector || '').trim(),
      cantidad_grupos_zootecnicos: formData.cantidad_grupos_zootecnicos || null,
    };

    try {
      const requestConfig = {
        headers: getAuthHeaders(),
      };
      const response = isEditMode
        ? await axios.put(apiUrl(`/api/ingresos_muestras/${sampleId}/`), cleanedData, requestConfig)
        : await axios.post(apiUrl('/api/ingresos_muestras/'), cleanedData, requestConfig);

      const protocoloMuestra = response.data.protocolo_muestra;
      const protocoloCaso = response.data.protocolo_general || cleanedData.protocolo_general;
      const successMessage = isEditMode
        ? `Muestra ${protocoloMuestra} actualizada correctamente.`
        : `Muestra registrada correctamente. Protocolo: ${protocoloMuestra}`;

      toast.success(successMessage);
      setSubmitNotice({ type: 'success', text: successMessage });
      setFormData(normalizeSampleData(response.data, protocoloCaso));

      if (location.state?.returnToPack === `/pack-toro/${protocoloCaso}`) {
        navigate(location.state.returnToPack, { state: { packFocus: protocoloMuestra } });
        return;
      }

      if (protocoloCaso && returnToCaseDetail) {
        navigate(`/ver-caso/${protocoloCaso}`, {
          state: isEditMode
            ? { muestraActiva: protocoloMuestra, muestraActualizada: protocoloMuestra }
            : { muestraActiva: protocoloMuestra, muestraRecienCreada: protocoloMuestra },
        });
        return;
      }

      if (!isEditMode && protocoloCaso) {
        navigate(`/ver-caso/${protocoloCaso}`, {
          state: { muestraActiva: protocoloMuestra, muestraRecienCreada: protocoloMuestra },
        });
      }
    } catch (error) {
      const backendMessage = formatSampleErrorMessage(error.response?.data);
      setSubmitNotice({ type: 'error', text: backendMessage });
      toast.error(backendMessage);
      console.error(error.response?.data || error.message);
    }
  };

  if (loadingSample) {
    return (
      <div className="surface-card">
        <InlineLoader label="Cargando muestra para editar..." />
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="surface-card">
      <div className="form-highlight mb-4">
        <div>
          <span className="section-kicker">{isEditMode ? 'Edicion' : 'Paso 2'}</span>
          <h3 className="form-title">{isEditMode ? 'Editar muestra' : 'Agregar muestra al caso'}</h3>
          <p className="page-subtitle mt-2 mb-0">
            {isEditMode
              ? 'Puedes ajustar los datos de la muestra sin salir del flujo del caso.'
              : 'Completa los datos de la muestra y vuelve al detalle del caso para seguir trabajando.'}
          </p>
        </div>
        <div className="protocol-chip">
          {formData.protocolo_muestra || formData.protocolo_general || 'Sin protocolo'}
        </div>
      </div>

      {submitNotice ? (
        <div className={`alert ${submitNotice.type === 'success' ? 'alert-success' : 'subtle-alert'} mb-4`} role="status">
          {submitNotice.text}
        </div>
      ) : null}

      <div className="mb-3">
        <label className="form-label">Protocolo General</label>
        {!isEditMode ? (
          <div className="row g-2 mb-2">
            <div className="col-sm-6">
              <label className="form-label small mb-1" htmlFor="protocolosDesde">Desde</label>
              <input
                id="protocolosDesde"
                type="date"
                className="form-control"
                value={rangoProtocolos.desde}
                max={rangoProtocolos.hasta || undefined}
                onChange={(event) => setRangoProtocolos((current) => ({ ...current, desde: event.target.value }))}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label small mb-1" htmlFor="protocolosHasta">Hasta</label>
              <input
                id="protocolosHasta"
                type="date"
                className="form-control"
                value={rangoProtocolos.hasta}
                min={rangoProtocolos.desde || undefined}
                onChange={(event) => setRangoProtocolos((current) => ({ ...current, hasta: event.target.value }))}
              />
            </div>
          </div>
        ) : null}
        {loading ? (
          <InlineLoader label="Cargando protocolos disponibles..." />
        ) : (
          <>
            <input
              type="text"
              name="protocolo_general"
              className={`form-control${isEditMode ? ' bg-light' : ''}`}
              value={formData.protocolo_general}
              onChange={handleChange}
              placeholder="Buscar o escribir, ej. 26-"
              list={!isEditMode ? 'protocolos-generales-disponibles' : undefined}
              autoComplete="off"
              readOnly={isEditMode}
            />
            {!isEditMode ? (
              <datalist id="protocolos-generales-disponibles">
                {protocolosDisponibles.map((protocolo) => (
                  <option key={protocolo} value={protocolo} />
                ))}
              </datalist>
            ) : null}

            <div className="form-text mt-2">
              {isEditMode
                ? 'El protocolo general queda fijo durante la edición para conservar la relación con el caso.'
                : protocolosDisponibles.length > 0
                  ? 'Escribe, por ejemplo, 26- para buscar entre los protocolos del rango seleccionado.'
                  : 'No hay protocolos en el rango de fechas seleccionado.'}
            </div>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Sector</label>
          <select
            name="sector"
            className="form-select"
            value={formData.sector}
            onChange={handleSectorChange}
            disabled={isEditMode}
          >
            <option value="">Seleccione un sector</option>
            {SAMPLE_SECTORS.map((sector) => (
              <option key={sector.value} value={sector.value}>
                {sector.label}
              </option>
            ))}
          </select>
          {isEditMode ? (
            <div className="form-text mt-2">
              El sector queda fijo en edición para mantener el protocolo de muestra original.
            </div>
          ) : null}
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="identificacionMuestra">Identificación de muestra / animal</label>
          <input id="identificacionMuestra" className="form-control" name="identificacion" maxLength={100}
            placeholder="Opcional"
            value={formData.identificacion}
            onChange={(event) => setFormData((prev) => ({ ...prev, identificacion: event.target.value }))} />
        </div>

        {isEditMode ? (
          <div className="mb-3">
            <label className="form-label">Protocolo de muestra</label>
            <input type="text" className="form-control bg-light" value={formData.protocolo_muestra} readOnly />
          </div>
        ) : (
          <div className="mb-3">
            <label className="form-label">Protocolo de muestra</label>
            <input type="text" className="form-control bg-light" value={protocoloMuestraPreview} readOnly />
            <div className="form-text mt-2">
              {protocoloMuestraEditable
                ? 'El prefijo se toma del sector. Solo editas el numero.'
                : 'Se genera automaticamente segun el sector seleccionado.'}
            </div>
            <div className="form-check mt-3">
              <input
                className="form-check-input"
                type="checkbox"
                checked={protocoloMuestraEditable}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setProtocoloMuestraEditable(enabled);
                  setProtocolExists(false);
                  if (enabled) {
                    const currentNumber = extractProtocolNumber(formData.protocolo_muestra);
                    setNumeroProtocoloManual(currentNumber);
                    setFormData((prev) => ({
                      ...prev,
                      protocolo_muestra: buildSampleProtocol(prev.sector, currentNumber),
                    }));
                    return;
                  }

                  setNumeroProtocoloManual('');
                  if (formData.sector) {
                    handleSectorChange({ target: { value: formData.sector } });
                  } else {
                    setFormData((prev) => ({ ...prev, protocolo_muestra: '' }));
                  }
                }}
                id="editarProtocoloMuestraCheck"
              />
              <label className="form-check-label" htmlFor="editarProtocoloMuestraCheck">
                Quiero editar manualmente el numero de protocolo de muestra
              </label>
            </div>

            {protocoloMuestraEditable ? (
              <div className="mt-3">
                <label className="form-label">Numero de protocolo</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-control"
                  value={numeroProtocoloManual}
                  onChange={handleNumeroProtocoloManualChange}
                  placeholder="Ej. 001 o 12045"
                  maxLength="5"
                />
                <div className="form-text mt-2">
                  Ingresa solo numeros, entre 3 y 5 digitos. El sistema arma el protocolo con el sector seleccionado.
                </div>
                {checkingProtocol ? (
                  <div className="form-text mt-2">Verificando disponibilidad del protocolo...</div>
                ) : null}
                {!checkingProtocol && protocoloMuestraEditable && protocolExists && protocoloMuestraPreview ? (
                  <div className="subtle-alert mt-2" role="alert">
                    El protocolo {protocoloMuestraPreview} ya existe. Elige otro numero.
                  </div>
                ) : null}
                {!checkingProtocol && protocoloMuestraEditable && !protocolExists && protocoloMuestraPreview ? (
                  <div className="form-text mt-2">El protocolo {protocoloMuestraPreview} esta disponible.</div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Fecha de recolección</label>
          <div className="input-group">
            <span className="input-group-text" aria-hidden="true">
              <FiCalendar />
            </span>
            <DatePicker
              selected={formData.fecha_recoleccion ? new Date(formData.fecha_recoleccion) : null}
              onChange={(date) => {
                setFormData((prev) => ({
                  ...prev,
                  fecha_recoleccion: date ? date.toISOString().split('T')[0] : '',
                }));
              }}
              dateFormat="yyyy-MM-dd"
              className="form-control"
              placeholderText="Selecciona una fecha"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={15}
              scrollableYearDropdown
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">¿AISA tomó muestra?</label>
          <select
            className="form-select"
            name="aisa_tomo_muestra"
            value={formData.aisa_tomo_muestra.toString()}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                aisa_tomo_muestra: event.target.value === 'true',
                nombre_operario_INTA: event.target.value === 'true' ? prev.nombre_operario_INTA : '',
              }))
            }
          >
            <option value="false">No</option>
            <option value="true">Sí</option>
          </select>
        </div>

        {formData.aisa_tomo_muestra ? (
          <div className="mb-3">
            <label className="form-label">Operario INTA</label>
            <select
              name="nombre_operario_INTA"
              className="form-select"
              value={formData.nombre_operario_INTA}
              onChange={handleChange}
            >
              <option value="">Seleccionar</option>
              {formData.nombre_operario_INTA && !operarioExistsInOptions ? (
                <option value={formData.nombre_operario_INTA}>{operarioLegacyLabel}</option>
              ) : null}
              {usuarios.map((user) => (
                <option key={user.id} value={user.username}>
                  {getUserDisplayName(user)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="mb-3">
          <label className="form-label">Grupo zootécnico</label>
          <select name="grupo_zootecnico" className="form-select" value={formData.grupo_zootecnico} onChange={handleChange}>
            <option value="">Seleccionar</option>
            <option value="VACUNO">Vacuno</option>
            <option value="OVINO">Ovino</option>
            <option value="CAPRINO">Caprino</option>
            <option value="PORCINO">Porcino</option>
            <option value="EQUINO">Equino</option>
            <option value="CAMELIDOS_SUDAMERICANOS">Camélidos Sudamericanos</option>
            <option value="CANINO">Canino</option>
            <option value="FELINO">Felino</option>
            <option value="AVES_DE_CORRAL">Aves de corral</option>
            <option value="ANIMALES_EXOTICOS">Animales exóticos</option>
            <option value="ANIMALES_SILVESTRES">Animales silvestres</option>
            <option value="VARIOS">Varios</option>
          </select>
        </div>

        {formData.tipo_muestra === 'ANIMAL' && formData.grupo_zootecnico === 'VARIOS' ? (
          <div className="mb-3">
            <label className="form-label">Cantidad de grupos zootécnicos</label>
            <input
              type="number"
              name="cantidad_grupos_zootecnicos"
              className="form-control"
              min="2"
              value={formData.cantidad_grupos_zootecnicos}
              onChange={handleChange}
              required
            />
          </div>
        ) : null}

        <div className="mb-3">
          <label className="form-label">Tipo de muestra</label>
          <select name="tipo_muestra" className="form-select" value={formData.tipo_muestra} onChange={handleChange} required>
            <option value="">Seleccionar</option>
            <option value="VEGETAL">Vegetal</option>
            <option value="ANIMAL">Animal</option>
            <option value="AGUA">Agua</option>
            <option value="OTRO">Otro</option>
          </select>
        </div>

        {formData.tipo_muestra === 'VEGETAL' ? (
          <>
            <div className="mb-3">
              <label className="form-label">Tipo de muestra vegetal</label>
              <select
                name="tipo_muestra_vegetal"
                className="form-select"
                value={formData.tipo_muestra_vegetal}
                onChange={handleChange}
                required
              >
                <option value="">Seleccionar</option>
                <option value="HOJA">Hoja</option>
                <option value="RAIZ">Raiz</option>
                <option value="FRUTO">Fruto</option>
                <option value="SEMILLA">Semilla</option>
                <option value="TALLO">Tallo</option>
                <option value="OTRO">Otro tipo de muestra vegetal</option>
              </select>
            </div>

            {formData.tipo_muestra_vegetal === 'OTRO' ? (
              <div className="mb-3">
                <label className="form-label">Describa el tipo de muestra vegetal</label>
                <textarea
                  name="descripcion_vegetal"
                  className="form-control"
                  rows="2"
                  value={formData.descripcion_vegetal}
                  onChange={handleChange}
                  required
                />
              </div>
            ) : null}
          </>
        ) : null}

        {formData.tipo_muestra === 'OTRO' ? (
          <div className="mb-3">
            <label className="form-label">Describa el otro tipo de muestra</label>
            <textarea
              name="otro_tipo_muestra"
              className="form-control"
              rows="2"
              value={formData.otro_tipo_muestra}
              onChange={handleChange}
              required
            />
          </div>
        ) : null}

        <div className="mb-3">
          <label className="form-label">Número de muestras</label>
          <input
            type="number"
            name="numero_de_muestras"
            className="form-control"
            min="1"
            value={formData.numero_de_muestras}
            onChange={handleChange}
          />
        </div>

        <div className="mb-4">
          <label className="form-label">Observaciones</label>
          <textarea
            name="observaciones"
            className="form-control"
            rows="3"
            maxLength={SAMPLE_OBSERVATIONS_MAX_LENGTH}
            aria-describedby="observaciones-help"
            value={formData.observaciones}
            onChange={handleChange}
          />
          <div id="observaciones-help" className="form-text mt-2">
            Maximo {SAMPLE_OBSERVATIONS_MAX_LENGTH} caracteres. {observacionesLength}/{SAMPLE_OBSERVATIONS_MAX_LENGTH} usados.
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 pt-2">
          {location.state?.returnToPack === `/pack-toro/${formData.protocolo_general}` && (
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(location.state.returnToPack, { state: { packFocus: sampleId } })}>Volver al Pack Toro sin guardar</button>
          )}
          <button type="submit" className="btn btn-brand">
            {isEditMode ? 'Guardar cambios de la muestra' : 'Agregar muestra'}
          </button>
        </div>
      </form>

      <ToastContainer />
    </div>
  );
};

export default IngresoMuestraForm;
